/**
 * Sidecar bboxes live in the emitted-GLB frame (glTF Y-up, recentred). The
 * (x, -z, y) swap below is the glTF Y-up -> 3D Tiles Z-up rotation the converter's
 * tile bounding volumes use; `tileset.root.computedTransform` then maps that frame
 * to ECEF, including any live model matrix.
 */
import {
	BoundingSphere,
	Cartesian3,
	HeadingPitchRange,
	Math as CesiumMath,
	Matrix4,
	type Cesium3DTileset,
	type Viewer,
} from 'cesium';

const FLY_DURATION_SECONDS = 1.0;
const MIN_RADIUS = 0.5;
const RANGE_FACTOR = 3;

export function unionBbox(boxes: (number[] | null | undefined)[]): number[] | null {
	let out: number[] | null = null;
	for (const box of boxes) {
		if (!box || box.length !== 6) continue;
		if (!out) {
			out = [...box];
			continue;
		}
		for (let axis = 0; axis < 3; axis++) {
			out[axis] = Math.min(out[axis], box[axis]);
			out[axis + 3] = Math.max(out[axis + 3], box[axis + 3]);
		}
	}
	return out;
}

export function bboxToBoundingSphere(
	bbox: number[],
	rootComputedTransform: Matrix4
): BoundingSphere {
	const [minX, minY, minZ, maxX, maxY, maxZ] = bbox;
	const local = new Cartesian3((minX + maxX) / 2, -(minZ + maxZ) / 2, (minY + maxY) / 2);
	const world = Matrix4.multiplyByPoint(rootComputedTransform, local, new Cartesian3());
	const radius = Math.max(MIN_RADIUS, 0.5 * Math.hypot(maxX - minX, maxY - minY, maxZ - minZ));
	return new BoundingSphere(world, radius);
}

export function flyToBbox(viewer: Viewer, tileset: Cesium3DTileset, bbox: number[] | null): void {
	if (viewer.isDestroyed()) return;
	if (!bbox) {
		viewer.flyTo(tileset, { duration: FLY_DURATION_SECONDS }).catch(() => undefined);
		return;
	}
	const sphere = bboxToBoundingSphere(bbox, tileset.root.computedTransform);
	viewer.camera.flyToBoundingSphere(sphere, {
		duration: FLY_DURATION_SECONDS,
		offset: new HeadingPitchRange(
			viewer.camera.heading,
			-CesiumMath.PI_OVER_SIX,
			sphere.radius * RANGE_FACTOR
		),
	});
}
